import { type ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { FapsChestDeal, FapsChestResponse, FapsLedgerEntry, QuestProgress } from "@gooner/shared";
import { formatFaps, wallTierFaps } from "@gooner/shared";
import { NewBadge } from "../../components/NewBadge";
import { useNavigate } from "react-router-dom";
import { apiErrorOf } from "../../lib/api";
import {
  LEDGER_COPY,
  type FapsOpenSource,
  trackFapsChestClaimed,
  trackFapsGetMoreClicked,
  trackFapsQuestBundleCompleted,
  trackFapsWallClicked,
  useFaps,
} from "../../lib/faps";
import { usePatreonLink } from "../../lib/patreon";
import { usePublicSettings } from "../../lib/publicSettings";
import { useServerClockOffset } from "../../lib/serverClock";
import { cx } from "../ui/primitives";
import { CROWN_GOLD, CrownMark } from "../ui/Crown";
import { FapsAmount } from "../ui/FapsAmount";
import { IconClose } from "../ui/icons";
import { AuthModal } from "./AuthModal";
import { GoldDialog } from "./GoldDialog";
import { useCountUp } from "../../lib/useCountUp";

/**
 * The FAPS dialog — the wallet, opened from the pill, the menu row, the profile card,
 * or the wall's "you're short" panel. GoldDialog's skeleton (portal, z-[60], the
 * panel card), because the two are neighbours in the same menu and should feel it.
 *
 * Six sections, top to bottom: the balance; the daily chest (the one thing to DO
 * here); today's quests (2026-09-09 — a second daily ritual, bundle-paid); how to
 * earn, with today's take against each cap; how to get more (the Premium stipend, a
 * redeem code, the Patreon shop); the last few ledger rows.
 *
 * EVERY NUMBER IN THE COPY COMES FROM SETTINGS — the chest's range, the caps, the
 * rewards, the stipend rate. WallBuyDialog's rule: a tunable spelled into a sentence
 * is a sentence that lies the day the tunable moves.
 *
 * The chest's countdown reads the SERVER's clock (useServerClockOffset): `nextAt` is a
 * server instant — UTC midnight — and a phone that is a minute out would otherwise
 * show a chest as ready a minute before the server agrees. The GoonSessionBar learned
 * this the hard way.
 */
export function FapsDialog({
  source,
  onClose,
  focus,
}: {
  source: FapsOpenSource;
  onClose: () => void;
  /** Scroll a section into view on open — the wall's short-balance door lands on the chest. */
  focus?: "chest";
}) {
  const { user, wallet, balance, refresh } = useFaps();
  const settings = usePublicSettings();
  const [authOpen, setAuthOpen] = useState(false);
  const [goldOpen, setGoldOpen] = useState(false);
  const chestRef = useRef<HTMLDivElement>(null);
  // The balance rolls in the TITLE now — it IS the title, so a chest opened in this dialog
  // lands where the eye already is. Hook at the top level, never inside a branch.
  const shown = useCountUp(balance);
  // `source` is the funnel dimension; the pill already tracked the open, so mounting
  // is NOT a second impression here (the fap_clicked lesson: one act, one event).
  void source;

  useEffect(() => {
    if (user) void refresh({ ifOlderThan: 10_000 });
  }, [user, refresh]);

  useEffect(() => {
    const el = focus === "chest" ? chestRef.current : null;
    el?.scrollIntoView({ block: "center" });
  }, [focus]);

  const s = settings?.faps;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-6 shadow-2xl"
      >
        <div className="mb-4 flex items-center justify-between">
          {/* The title IS the balance: crown then the number the account holds (icon, then
              value). Signed out — or before the first wallet read — there is no number, so
              it falls back to the word. The old big hero below is gone; this is it. */}
          <h2 className="flex items-center gap-2 text-lg font-semibold text-zinc-100" style={user && balance !== null ? { color: CROWN_GOLD } : undefined}>
            <CrownMark size={18} />
            <span className="tabular-nums">{user && balance !== null ? formatFaps(shown ?? balance) : "Faps"}</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-zinc-400 hover:bg-[var(--color-panel-2)] hover:text-white"
          >
            <IconClose />
          </button>
        </div>

        {!user ? (
          <>
            <p className="mb-4 text-sm text-zinc-300">
              Faps live on an account. Fap clips, open a daily chest, keep a streak.
            </p>
            <button
              type="button"
              onClick={() => setAuthOpen(true)}
              className="w-full rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-sm font-semibold text-black hover:brightness-110"
            >
              Create a free account
            </button>
            {authOpen ? (
              <AuthModal initialMode="register" reason="Create a free account to start earning Faps." onClose={() => setAuthOpen(false)} />
            ) : null}
          </>
        ) : (
          <>
            <div ref={chestRef}>
              {wallet ? <DailyChest wallet={wallet} /> : <Skeleton lines={3} />}
            </div>

            {wallet ? <DailyQuests wallet={wallet} /> : null}

            {wallet && s ? <EarnList wallet={wallet} settings={s} /> : null}

            {wallet && s ? <SpendIt balance={wallet.balance} settings={s} onClose={onClose} /> : null}

            <GetMore
              stipendPerCent={s?.stipendPerEntitledCent ?? 0}
              onPremium={() => {
                trackFapsGetMoreClicked("premium");
                setGoldOpen(true);
              }}
            />

            {wallet ? <Ledger rows={wallet.recent} /> : null}

            {goldOpen ? <GoldDialog source="faps" onClose={() => setGoldOpen(false)} /> : null}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

// --- sections ---------------------------------------------------------------------------

function DailyChest({ wallet }: { wallet: NonNullable<ReturnType<typeof useFaps>["wallet"]> }) {
  const { dealChest, pickChest } = useFaps();
  /*
   * The chest is a small game:
   *
   *   ready ──"Open"──▶ deal ──▶ grid ──pick──▶ reveal
   *
   * A GOONCHA used to stand between "Open" and the deal (dropped 2026-09-09: an
   * account and once-a-day are the gate). The server deals nine tiles and draws a
   * prize behind each; the pick is honest (the draws were made before it), the prize
   * lands, and every tile turns over so the other eight are seen.
   */
  const [stage, setStage] = useState<"ready" | "grid">("ready");
  const [deal, setDeal] = useState<FapsChestDeal | null>(null);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState<FapsChestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const offset = useServerClockOffset();
  const { available, nextAt } = wallet.chest;

  // A callback, not an effect: an effect keyed on `busy` cancelled its own request the
  // moment it set busy — "Dealing…" forever.
  const dealNow = async () => {
    setBusy(true);
    setError(null);
    try {
      const d = await dealChest();
      setDeal(d);
      setStage("grid");
    } catch (e) {
      setError((apiErrorOf(e)?.error ?? "Couldn't open the chest — try again.").replace(/^\d+: /, ""));
      setStage("ready");
    } finally {
      setBusy(false);
    }
  };

  const pick = async (index: number) => {
    if (!deal || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await pickChest(deal.dealId, index);
      setReveal(r);
      trackFapsChestClaimed(r.amount);
    } catch (e) {
      // A stale deal (ten minutes passed, midnight passed): back to the start.
      setError((apiErrorOf(e)?.error ?? "Couldn't open the chest — try again.").replace(/^\d+: /, ""));
      setDeal(null);
      setStage("ready");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cx(
        "mb-4 rounded-xl border p-4",
        available || reveal
          ? "border-[#f5c518]/40 bg-[#f5c518]/5"
          : "border-[var(--color-edge)] bg-[var(--color-panel-2)]/40",
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-zinc-100">Daily chest</p>
        {reveal ? (
          <span className="text-lg font-semibold">
            <FapsAmount n={reveal.amount} signed size={16} />
          </span>
        ) : null}
      </div>

      {reveal && deal ? (
        <ChestGrid tiles={deal.tiles} reveal={reveal} onPick={() => undefined} busy={false} />
      ) : stage === "grid" && deal ? (
        <>
          <p className="mb-2 text-sm font-medium text-zinc-100">Pick your favourite to reveal your prize</p>
          <ChestGrid tiles={deal.tiles} reveal={null} onPick={(i) => void pick(i)} busy={busy} />
          {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
        </>
      ) : available ? (
        <>
          <p className="mb-3 text-xs text-zinc-400">Nine clips, a prize under each. Pick one.</p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void dealNow()}
            className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-black transition hover:brightness-110 disabled:opacity-60"
            style={{ backgroundColor: CROWN_GOLD }}
          >
            {busy ? "Dealing…" : "Open the chest"}
          </button>
          {error ? <p className="mt-2 text-xs text-red-400">{error}</p> : null}
        </>
      ) : (
        <p className="text-xs text-zinc-400">
          Opened today. Next chest in <Countdown until={nextAt} offset={offset} />.
        </p>
      )}
    </div>
  );
}

/**
 * The nine tiles — GOONCHA's grid, worn as a game: looping, muted, one tap. Before the
 * pick every tile is a door; after it every tile turns over to its prize, the picked
 * one lit and the rest dimmed, so the person sees what the other eight hid.
 */
function ChestGrid({
  tiles,
  reveal,
  onPick,
  busy,
}: {
  tiles: string[];
  reveal: FapsChestResponse | null;
  onPick: (index: number) => void;
  busy: boolean;
}) {
  return (
    <div className="grid grid-cols-3 gap-1">
      {tiles.map((src, i) => {
        const picked = reveal?.index === i;
        const prize = reveal?.prizes[i];
        return (
          <button
            key={`${src}-${i}`}
            type="button"
            onClick={() => !reveal && !busy && onPick(i)}
            disabled={!!reveal || busy}
            aria-label={reveal ? `Chest tile ${i + 1}: ${prize ?? 0}` : `Chest tile ${i + 1}`}
            className={cx(
              "relative aspect-square overflow-hidden rounded-md bg-black transition-all",
              !reveal && !busy && "hover:ring-2 hover:ring-inset hover:ring-[#f5c518]",
              reveal && picked && "ring-2 ring-inset ring-[#f5c518] scale-[1.03]",
              reveal && !picked && "opacity-40",
            )}
          >
            <video
              src={src}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="pointer-events-none h-full w-full bg-[var(--color-panel-2)] object-cover"
            />
            {reveal ? (
              <span className="absolute inset-0 flex items-center justify-center bg-black/55">
                <FapsAmount n={prize ?? 0} signed size={picked ? 18 : 12} className={picked ? "text-xl font-bold" : "text-sm font-semibold"} />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * TODAY'S QUESTS (2026-09-09). Two or three tasks, drawn fresh at UTC midnight, the
 * same shape for every account — Premium changes only the bundle's SIZE, never which
 * quests show or how many are assigned. Nothing pays per quest; the footer's number is
 * the whole reward, and it only lands once every row here is checked off.
 *
 * The rising edge (not-yet-complete → complete) fires the analytics event exactly
 * once per day, from a ref rather than a state flag — the wallet re-fetches on its own
 * clock and re-rendering with the same `allComplete: true` must not fire twice.
 */
/** Until when Daily Quests wears its NEW! badge — a month after launch (2026-09-09);
 *  see NewBadge's own header for why this constant is not shared with any other
 *  surface's. Extend by moving this date, nothing else. */
const QUESTS_NEW_UNTIL = Date.UTC(2026, 9, 9);

const QUEST_LABEL: Record<QuestProgress["id"], string> = {
  fap_clips: "Fap {n} clips",
  scroll_pages: "Scroll {n} pages on Infinite Scroll",
  battle_rounds: "Play {n} rounds of Goonette of the Hill",
  edge: "Edge {n} times",
  wall_bid: "Bid on a Community Wall square",
  create_view: "Save a new view",
  open_chest: "Open today's chest",
  create_collection: "Save a new collection",
  start_session: "Start a Goon session",
  watch_clips: "Watch {n} clips",
  // {name} is today's ONE rotating pick (a wall, or a Quick Setup — pickWatchTarget on
  // the server), so this reads "Watch Tit Ladder for 5 min" one day and "Watch the
  // Community Wall for 5 min" the next.
  watch_view: "Watch {name} for {n} min",
  pair_device: "Pair a screen from the remote",
  room_minutes: "Hang out in a Goon Room for {n} min",
};

/** `watchTarget` is only ever needed for the one quest whose label names a surface —
 *  see QUEST_LABEL.watch_view. Absent for every other id, harmlessly: the label has no
 *  `{name}` to replace. */
function questLabel(q: QuestProgress, watchTarget?: { name: string }): string {
  return QUEST_LABEL[q.id].replace("{n}", String(q.target)).replace("{name}", watchTarget?.name ?? "today's pick");
}

function DailyQuests({ wallet }: { wallet: NonNullable<ReturnType<typeof useFaps>["wallet"]> }) {
  const { quests } = wallet;
  const completedBefore = useRef(false);
  useEffect(() => {
    if (quests.allComplete && !completedBefore.current) {
      trackFapsQuestBundleCompleted(quests.bundleReward);
    }
    completedBefore.current = quests.allComplete;
  }, [quests.allComplete, quests.bundleReward]);

  if (quests.items.length === 0) return null;
  return (
    <div className="mb-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
        Today&rsquo;s quests
        {Date.now() < QUESTS_NEW_UNTIL ? <NewBadge variant="inline" /> : null}
      </p>
      <ul className="space-y-2.5">
        {quests.items.map((q) => (
          <li key={q.id}>
            <div className="flex items-baseline justify-between">
              <span className={cx("text-sm", q.completed ? "text-zinc-500 line-through" : "text-zinc-200")}>
                {q.completed ? <span aria-hidden className="mr-1 not-italic text-[var(--color-accent)]">✓</span> : null}
                {questLabel(q, quests.watchTarget)}
              </span>
            </div>
            {/* The same bar the earn list's caps use — for a did-it-happen quest
                (target 1: open the chest, bid on the wall, save a view, start a
                session, pair a screen) it is empty then full in one step; for the
                counting quests (fap N clips, scroll N pages, edge N times, watch N
                clips, watch N minutes) it moves with every bump. One component
                either way, so a quest reads like every other daily meter. */}
            <Meter earned={q.count} cap={q.target} />
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-zinc-500">
        Finish every quest today to earn <FapsAmount n={quests.bundleReward} size={10} signed />.
      </p>
    </div>
  );
}

function EarnList({
  wallet,
  settings,
}: {
  wallet: NonNullable<ReturnType<typeof useFaps>["wallet"]>;
  settings: NonNullable<ReturnType<typeof usePublicSettings>>["faps"];
}) {
  const { user } = useFaps();
  // The crown's rate is the reader's TIER's rate; the cap already is (the wallet reports
  // the tier's cap), so the two agree with what a tap actually pays.
  const premium = (user?.goldUntil ?? 0) > Date.now();
  const watchReward = Math.round(settings.watchReward * (premium ? Math.max(1, settings.watchMultiplierPremium) : 1));
  const rows: Array<{ title: string; reward: ReactNode; earned: number; cap: number; unit?: string }> = [
    {
      // 2026-09-09: once per DISTINCT clip a day now, capped at N clips — not N Faps —
      // so the title and the meter both read in clips.
      title: "Fap a clip",
      reward: <FapsAmount n={premium ? settings.fapRewardPremium : settings.fapReward} signed size={10} />,
      earned: wallet.today.fap.earned,
      cap: wallet.today.fap.cap,
    },
    {
      // 2026-09-09: a flat once-a-day award for crossing the minute threshold — no
      // session, no per-minute rate. The meter tracks MINUTES against the threshold,
      // clamped so a long sitting never shows past the cap.
      title: `Watch ${settings.watchThresholdMinutes} min today`,
      reward: watchReward > 0 ? <FapsAmount n={watchReward} signed size={10} /> : "—",
      earned: Math.min(wallet.today.watch.minutes, wallet.today.watch.thresholdMinutes),
      cap: wallet.today.watch.thresholdMinutes,
    },
  ];
  return (
    <div className="mb-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">How to earn</p>
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.title}>
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-zinc-200">{r.title}</span>
              <span className="text-xs">{r.reward}</span>
            </div>
            <Meter earned={r.earned} cap={r.cap} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Today's take against a cap — PercentileBar's bar without the percentile copy. */
function Meter({ earned, cap }: { earned: number; cap: number }) {
  const full = cap > 0 && earned >= cap;
  const pct = cap > 0 ? Math.min(100, (earned / cap) * 100) : 0;
  return (
    <div className="mt-1">
      <div className="h-1.5 overflow-hidden rounded-full bg-[var(--color-panel-2)]">
        <div
          className={cx(
            "h-full rounded-full motion-safe:transition-[width] motion-safe:duration-500 motion-safe:ease-out",
            full ? "bg-[#f5c518]" : "bg-[var(--color-accent)]",
          )}
          style={{ width: `${earned > 0 ? Math.max(2, pct) : 0}%` }}
        />
      </div>
      <div className="mt-0.5 text-[11px] tabular-nums text-zinc-500">
        {formatFaps(earned)} / {formatFaps(cap)} today{full ? " — back tomorrow" : ""}
      </div>
    </div>
  );
}

/**
 * "Spend it" — the door from the wallet to the wall, kept GENERAL on purpose (the owner's
 * call, 2026-09-08): "capture squares, bid" rather than a price list. The wall is where
 * Faps are spent — bid for a square, hold it, defend it — and quoting the small/marquee
 * numbers here only dated the copy and read as fine print. The board itself carries the
 * live prices; this is the invitation, not the receipt.
 *
 * The one number still read is `short` — could they afford the cheapest square as they
 * left — and it never shows; it only rides on the analytics so "do the short ones still
 * go?" stays answerable. `small === null` (a misconfigured ladder) hides the door rather
 * than invite someone to a wall with no price. Same-tab, like every door in the app now.
 */
function SpendIt({
  balance,
  settings,
  onClose,
}: {
  balance: number;
  settings: NonNullable<ReturnType<typeof usePublicSettings>>["faps"];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const small = wallTierFaps(settings, "small");
  if (small === null) return null;
  const short = balance < small;
  return (
    <div className="mb-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Spend it</p>
      <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-edge)] px-3 py-2.5">
        <p className="text-sm text-zinc-300">
          Bid for a square on the wall — capture one and your clips play on the big screen.
        </p>
        <button
          type="button"
          onClick={() => {
            trackFapsWallClicked("wallet", short);
            onClose();
            navigate("/screen?code=0");
          }}
          className="shrink-0 rounded-lg bg-[#f5c518] px-3 py-1.5 text-sm font-semibold text-black hover:brightness-110"
        >
          Go to the wall
        </button>
      </div>
    </div>
  );
}

/**
 * "Get more" — the ways to top up a balance that live inside the wallet. Today that is the
 * monthly Premium stipend; a bought pack is redeemed by LINK now (the operator sends a
 * /faps/redeem link after a Patreon purchase), never by pasting a code here, so there is no
 * redeem form. When there is nothing to show — no stipend configured — the whole section is
 * absent rather than an empty heading.
 */
function GetMore({
  stipendPerCent,
  onPremium,
}: {
  stipendPerCent: number;
  onPremium: () => void;
}) {
  const patreon = usePatreonLink();
  const { user } = useFaps();
  const goldNow = (user?.goldUntil ?? 0) > Date.now();
  const monthly = patreon.state?.monthlyPrice ?? null;
  const stipend = monthly !== null ? Math.round(monthly * 100 * stipendPerCent) : null;
  if (stipendPerCent <= 0) return null;
  return (
    <div className="mb-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Get more</p>
      <div className="space-y-2">
        <Door
          title={
            stipend !== null ? (
              <span className="inline-flex items-center gap-1">
                <FapsAmount n={stipend} size={12} gold={false} /> every month with Premium
              </span>
            ) : (
              "A monthly stipend with Premium"
            )
          }
          sub={goldNow ? "You're Premium — it lands on your membership's clock." : "Every month you're a member."}
          action={goldNow ? null : { label: "See Premium", onClick: onPremium }}
        />
      </div>
    </div>
  );
}

function Door({
  title,
  sub,
  action,
}: {
  title: ReactNode;
  sub: string;
  action: { label: string; onClick: () => void; href?: string } | null;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-edge)] px-3 py-2.5">
      <div className="min-w-0">
        <p className="text-sm font-medium text-zinc-100">{title}</p>
        <p className="text-xs text-zinc-500">{sub}</p>
      </div>
      {action ? (
        action.href ? (
          /* An outbound link served from settings: new tab, no opener, the PatreonPremium discipline. */
          <a
            href={action.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={action.onClick}
            className="shrink-0 whitespace-nowrap rounded-md border border-white/15 px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-white/10"
          >
            {action.label}
          </a>
        ) : (
          <button
            type="button"
            onClick={action.onClick}
            className="shrink-0 whitespace-nowrap rounded-md border border-white/15 px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-white/10"
          >
            {action.label}
          </button>
        )
      ) : null}
    </div>
  );
}

function Ledger({ rows }: { rows: FapsLedgerEntry[] }) {
  const offset = useServerClockOffset();
  const now = Date.now() + offset;
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Recent</p>
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-500">Nothing yet — crown a clip.</p>
      ) : (
        // The server now sends only the last 3 (fapsLedgerForUser(user.id, 3) in
        // FapsService.wallet, 2026-09-09) — no client-side slice needed any more.
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.id} className="flex items-baseline justify-between text-xs">
              <span className="min-w-0 truncate text-zinc-300">
                {LEDGER_COPY[r.kind]}
                {/* The note carries the WHO/WHICH for the rows where that is the point — an
                    admin adjustment's reason, and an unseated row's "@who took your square". */}
                {(r.kind === "admin" || r.kind === "unseated") && r.note ? (
                  <span className="text-zinc-500"> — {r.note}</span>
                ) : null}
              </span>
              <span className="ml-3 flex shrink-0 items-baseline gap-2">
                <span className="text-zinc-600">{ago(now - r.createdAt)}</span>
                <FapsAmount n={r.delta} signed size={10} gold={r.delta >= 0} className={cx("font-medium", r.delta < 0 && "text-zinc-400")} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Skeleton({ lines }: { lines: number }) {
  return (
    <div className="mb-4 space-y-2 rounded-xl border border-[var(--color-edge)] p-4">
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="h-3 animate-pulse rounded bg-white/10" style={{ width: `${70 - i * 15}%` }} />
      ))}
    </div>
  );
}

// --- small helpers ------------------------------------------------------------------------

/** "13h 22m" until a server instant; ticks once a second while mounted. */
function Countdown({ until, offset }: { until: number; offset: number }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = Math.max(0, until - (Date.now() + offset));
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return <span className="tabular-nums text-zinc-200">{h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`}</span>;
}

function ago(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return "now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
